import { formatEasternDate, sortByDateOfService } from 'helpers/dates';
import { BillingChargeProcedure } from 'store/types';

export const getRenderingAndBilling = (renderingAndBilling?: string | null): string => {
	// Sleep Solutions, LLC is retired; legacy charges bill and render as TestSmarter.
	if (renderingAndBilling === 'SLEEP_SOLUTIONS') {
		return 'TestSmarter, Inc.';
	}

	if (renderingAndBilling === 'SLEEP_SOLUTIONS_NORTH_FL') {
		return 'TestSmarter DBA Sleep Solutions of North Florida';
	}

	if (renderingAndBilling === 'TEST_SMARTER') {
		return 'TestSmarter, Inc.';
	}

	return '--';
}

export const getBillingTestTypeLabel = (testType?: string | null): string | null => {
	if (testType === 'POX') {
		return 'Pulse-Oximetry';
	}

	if (testType === 'COX') {
		return 'Capnography';
	}

	if (testType === 'HST') {
		return 'Home Sleep Test';
	}

	if (testType === 'PSG') {
		return 'In-Lab Sleep Test';
	}

	return testType || null;
}

export const getShortChargeIdValue = (chargeId?: string | null): string => {
	if (!chargeId) {
		return '--------';
	}

	return String(chargeId).slice(0, 8);
}

export const getShortChargeId = (chargeId?: string | null): string => {
	return `[${getShortChargeIdValue(chargeId)}]`;
}

export const getChargeDropdownLabel = (charge?: {
	id?: string | null
	orderId?: string | number | null
	orderTestType?: string | null
	totalPatientAmount?: string | number | null
	totalPlanAmount?: string | number | null
}): string => {
	const chargeId = getShortChargeId(charge?.id);
	const testType = getBillingTestTypeLabel(charge?.orderTestType);
	const typeAndOrder = testType && charge?.orderId ? `${testType} #${charge.orderId}, ` : '';

	return `${chargeId} ${typeAndOrder}Patient - ${charge?.totalPatientAmount || 0}$, Plan - ${charge?.totalPlanAmount || 0}$`;
}

// react-select filter for charge dropdowns: matches the typed value as a
// prefix of the full charge UUID or of the order ID (the short [8-char] id in
// the label only covers part of the UUID), plus plain label substrings.
export const chargeOptionFilter = (
	// Loosely typed so react-select keeps inferring its Option generic from the
	// options array rather than from this parameter.
	option: { label: string; data?: any },
	rawInput: string,
): boolean => {
	const input = rawInput.trim().toLowerCase();

	if (!input) {
		return true;
	}

	return (
		String(option.data?.chargeId ?? '').toLowerCase().startsWith(input) ||
		String(option.data?.orderId ?? '').startsWith(input) ||
		option.label.toLowerCase().includes(input)
	);
}

// Whether a claim rank's payer identity — the plan, member id and group id it
// was billed under — stays locked on the charge screen.
//
// It locks once that rank's claim has gone out, so nobody edits the identity a
// live claim was submitted under by accident. A payer rejection is the
// exception the lock has to make: a rejected claim never entered adjudication,
// and what has to change for it to adjudicate is usually the very identity the
// lock is holding (A3:88, "entity not eligible for benefits for the submitted
// dates of service", is the everyday case — the claim went to the wrong plan).
// Resubmitting writes a new control number for the rank, which clears its
// payer verdict, so the fields lock again on their own once the fix is sent.
// An open denial unlocks them too: a claim denied for the wrong payer is
// corrected and resubmitted from the denial, which closes it as rebilled.
export const isPayerIdentityLocked = (
	submissionStatus?: string | null,
	payerClaimStatus?: { status?: string | null } | null,
	hasOpenDenial?: boolean,
): boolean => submissionStatus === 'SUBMITTED' && payerClaimStatus?.status !== 'REJECTED' && !hasOpenDenial;

export type ClaimPayerRank = 'primary' | 'secondary' | 'tertiary';

export type ChargeSubmissionMethods = {
	claimSubmissionMethod?: string | null
	secondaryClaimSubmissionMethod?: string | null
	tertiaryClaimSubmissionMethod?: string | null
};

// How the claim to a rank's payer goes out. Each hop follows its own payer,
// so an electronic Medicare primary can sit in front of a paper-only
// supplement. The server resolves every rank; the fallback to the primary only
// covers a row that predates per-rank methods, as the migration did.
export const getClaimSubmissionMethod = (
	charge: ChargeSubmissionMethods | null | undefined,
	rank: ClaimPayerRank,
): 'ELECTRONIC' | 'PAPER' => {
	const own = rank === 'secondary'
		? charge?.secondaryClaimSubmissionMethod
		: rank === 'tertiary' ? charge?.tertiaryClaimSubmissionMethod : charge?.claimSubmissionMethod;

	return (own || charge?.claimSubmissionMethod) === 'PAPER' ? 'PAPER' : 'ELECTRONIC';
};

export const isPaperClaim = (
	charge: ChargeSubmissionMethods | null | undefined,
	rank: ClaimPayerRank,
): boolean => getClaimSubmissionMethod(charge, rank) === 'PAPER';

type CobActionCharge = { cobRank?: string | null } & ChargeSubmissionMethods;

const cobActionRank = (charge: CobActionCharge): 'secondary' | 'tertiary' => (
	charge?.cobRank === 'tertiary' ? 'tertiary' : 'secondary'
);

// The COB button's wording for the hop that is next. A paper hop is marked
// submitted rather than sent, and the button says so — including the
// "anyway" button on a claim the payer already crossed over.
export const getCobActionLabel = (charge: CobActionCharge, { anyway = false } = {}): string => {
	const rank = cobActionRank(charge);
	const isPaper = isPaperClaim(charge, rank);

	if (anyway) {
		return isPaper ? 'Mark as submitted anyway' : 'Submit anyway';
	}

	const claim = rank === 'tertiary' ? 'Tertiary COB claim' : 'COB claim';

	return isPaper ? `Mark ${claim} as submitted` : `Submit ${claim}`;
};

// The same button's tooltip.
export const getCobActionTitle = (charge: CobActionCharge): string => {
	const rank = cobActionRank(charge);

	return isPaperClaim(charge, rank)
		? `Mark the paper COB claim to the ${rank} payer as submitted once it is printed and mailed`
		: `Submit a COB claim to the ${rank} payer`;
};

// Shared confirmation copy for submitting a COB claim, so the charge page and
// the patient payments table always prompt with the same wording. The rank
// decides the wording: the secondary hop carries the primary's adjudication,
// the tertiary hop carries both prior payers'.
export const getCobConfirmOptions = (
	charge?: {
		transferAmount?: number | null
		secondaryPayerName?: string | null
		tertiaryPayerName?: string | null
		cobStatus?: string | null
		crossedOverAt?: string | Date | null
	} & ChargeSubmissionMethods,
	cobRank: 'secondary' | 'tertiary' = 'secondary',
) => {
	const isTertiary = cobRank === 'tertiary';
	const payerName = (isTertiary ? charge?.tertiaryPayerName : charge?.secondaryPayerName)
		|| `the ${cobRank} payer`;
	// The payer already forwarded this claim, so submitting sends a second copy.
	// Worth doing when a crossover stalls, but the biller has to mean it.
	const isCrossedOver = charge?.cobStatus === 'CROSSED_OVER';
	const crossedOverWarning = isCrossedOver
		? `The payer forwarded this claim to ${payerName} itself`
			+ `${charge?.crossedOverAt ? ` on ${formatEasternDate(charge.crossedOverAt)}` : ''}`
			+ `, so ${payerName} should already be adjudicating it. Submitting now sends a second claim, `
			+ `which the payer may reject as a duplicate. `
		: '';
	const transferBalance = `$${Number(charge?.transferAmount || 0).toFixed(2)}`;

	// A paper hop sends nothing anywhere: submitting it only records that it
	// was mailed. Saying "submit" here would let a biller believe the claim
	// went to Optum while the envelope was never printed.
	if (isPaperClaim(charge, cobRank)) {
		const form = isTertiary ? 'tertiary COB CMS-1500' : 'COB CMS-1500';
		const eobs = isTertiary ? 'the primary and secondary payers\' EOBs' : 'the primary payer\'s EOB';

		return {
			title: isTertiary ? 'Mark paper Tertiary COB claim as submitted' : 'Mark paper COB claim as submitted',
			description: crossedOverWarning
				+ `${payerName} takes claims on paper, so nothing is sent to Optum. `
				+ `Print the ${form} from the charge page and mail it with ${eobs} `
				+ `for the transfer balance of ${transferBalance}. Mark it as submitted now?`,
			yesAction: 'Mark as submitted',
			noAction: 'Cancel',
		};
	}

	return {
		title: isCrossedOver
			? 'Submit anyway — this claim already crossed over'
			: (isTertiary ? 'Submit Tertiary COB claim' : 'Submit COB claim to secondary'),
		description:
			crossedOverWarning
			// The transfer balance, not the patient balance: a COB hop bills what the
			// prior payer passed on, and the patient balance stays $0.00 until the
			// last payer adjudicates.
			+ `Submit the transfer balance of `
			+ `${transferBalance} to `
			+ `${payerName} as a coordination-of-benefits claim? `
			+ (isTertiary
				? `The primary and secondary payers' adjudications and EOBs are included automatically.`
				: `The primary payer's adjudication and EOB are included automatically.`),
		yesAction: isCrossedOver
			? 'Submit anyway'
			: (isTertiary ? 'Submit Tertiary COB claim' : 'Submit COB claim'),
		noAction: 'Cancel',
	};
};

// The prompt before sending a COB claim Optum already accepted to its payer
// again. It goes out as a new submission under a new Claim Identifier —
// right for a claim the payer rejected or one to correct, so the prompt says
// what is about to happen. Shared by the charge page and the payments table.
export const getCobResubmitConfirmOptions = (
	charge?: {
		secondaryPayerName?: string | null
		tertiaryPayerName?: string | null
	},
	cobRank: 'secondary' | 'tertiary' = 'secondary',
) => {
	const isTertiary = cobRank === 'tertiary';
	const claimLabel = isTertiary ? 'Tertiary COB claim' : 'COB claim';
	const payerName = (isTertiary ? charge?.tertiaryPayerName : charge?.secondaryPayerName)
		|| `the ${cobRank} payer`;

	return {
		title: `Re-submit ${claimLabel}?`,
		description: `This sends the ${claimLabel} to ${payerName} again as a new submission, `
			+ 'under a new Claim Identifier. Use it when the payer rejected the claim or it has to be corrected; '
			+ "if the payer already processed it, set Claim Type to Replacement first so the payer's Claim Number is cited.",
		yesAction: `Re-submit ${claimLabel}`,
		noAction: 'Cancel',
	};
};

// Optum's reason a COB claim was refused, short enough for a toast. The COB
// claim now goes out inside the request, so the reason arrives with it; the
// charge page shows it in full once refreshed.
export const describeCobSubmissionError = (error: unknown): string => {
	const message = (error as { data?: { message?: string } })?.data?.message || '';
	const reason = message.replace(/^COB claim submission failed:\s*/i, '').trim();

	if (!reason) {
		// No answer at all (a timeout): the claim may still be on its way, and the
		// refreshed charge says whether it went out.
		return 'No answer came back; check the charge to see whether the claim went out.';
	}

	return reason.length > 300 ? `${reason.slice(0, 300)}…` : reason;
};

// The charge's waterfall state, shared by the payments table, the charge page
// and the COB queue so a biller reads the same word everywhere.
export const RESPONSIBILITY_LABELS: Record<string, string> = {
	PLAN_PENDING: 'With plan',
	TRANSFERRED: 'Transferred',
	CROSSED_OVER: 'Crossed over',
	PATIENT: 'Patient',
};

// A forwarded remittance and a crossed-over charge are the same fact, so they
// carry the same colour as the ERA claims table's Forwarded pill.
export const FORWARDED_COLOR = '#3CAEFF';

const RESPONSIBILITY_COLORS: Record<string, string> = {
	CROSSED_OVER: FORWARDED_COLOR,
	TRANSFERRED: '#FD7E14',
	PATIENT: '#6C757D',
	PLAN_PENDING: '#6C757D',
};

export const responsibilityColor = (state?: string | null): string =>
	RESPONSIBILITY_COLORS[state || ''] || '#6C757D';

export const chargeBadgeStyle = {
	display: 'inline-block',
	backgroundColor: '#007bff',
	color: '#ffffff',
	borderRadius: '4px',
	padding: '0.25rem 0.5rem',
	fontSize: '0.813rem',
	fontWeight: 600,
	lineHeight: 1.2,
} as const;

// A procedure row on the charge form, remembering its place on the saved charge.
export type ProcedureFormRow = BillingChargeProcedure & { storedPosition?: number };

/**
 * The charge's procedures as the form lays them out: earliest date of service
 * first. The form sorts only when it loads and after a save, so a row never
 * moves while the biller is typing into it.
 */
export const toProcedureFormRows = (procedures: BillingChargeProcedure[]): ProcedureFormRow[] =>
	sortByDateOfService(
		procedures.map((procedure, storedPosition) => ({ ...procedure, storedPosition })),
		(row) => row.startDos,
	);

/**
 * The procedures to save, in the charge's own order: saved lines keep their
 * places and new lines follow, earliest first. A posted payment finds its line
 * by position, so reordering saved lines would move its money onto another
 * date of service. Only the screen is sorted.
 */
export const toSavedProcedures = (rows: ProcedureFormRow[]): BillingChargeProcedure[] => {
	const saved = rows
		.filter((row) => row.storedPosition !== undefined)
		.sort((a, b) => Number(a.storedPosition) - Number(b.storedPosition));
	const added = sortByDateOfService(
		rows.filter((row) => row.storedPosition === undefined),
		(row) => row.startDos,
	);

	return [...saved, ...added].map(({ storedPosition: _storedPosition, ...procedure }) => procedure);
};
